import { ArrowRightIcon } from '@primer/octicons-react';
import { LTText } from '@lambdatestincprivate/lt-components';
import { LoadingJourney } from '../states/LoadingJourney';
import { ATTENTION_DISPLAY_ITEMS } from '../../config';
import { usePackState } from '../../data/pack-store';
import { StatusIcon } from '../icons/StatusIcon';
import styles from './need-attention.module.css';

/**
 * "Need your attention" — rendered from the pack's root failure.yaml index
 * plus each referenced per-step failure detail (bug title ?? error.message
 * is the row title, triage.rca.root_cause the tooltip). Hidden entirely
 * for packs without failure data. Layout mirrors Figma node 30776:15149
 * (Primer Label pills, 56px rows, link + trailing arrow); rows are
 * buttons that open the Issues tab's RCA drawer ("View RCA" when triaged,
 * "Triage" when not — same destination, the drawer IS the triage record).
 *
 * Severity pills map triage.severity (critical→P0, major→P1, minor→P2,
 * cosmetic→Gap; palette from Figma): P0/P2 danger #cf222e · P1 attention
 * #9a6700 · Gap severe #bc4c00. Packs without authored severity (older
 * producers / untriaged) render the row without a pill — no invented P0.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var SEVERITY_BG = {
  P0: '#cf222e',
  P1: '#9a6700',
  P2: '#cf222e',
  Gap: '#bc4c00'
};
export function NeedAttention(_ref) {
  var onOpenIssue = _ref.onOpenIssue;
  var attention = usePackState(function (s) {
    return s.attention;
  });
  var phase = usePackState(function (s) {
    return s.attentionPhase;
  });
  // Until the failure index settles we don't know whether this pack HAS
  // failures — a quiet loader card beats flashing an absent block.
  if (!attention && phase === 'loading') {
    return /*#__PURE__*/_jsx("section", {
      className: styles.card,
      "aria-label": "Need your attention",
      children: /*#__PURE__*/_jsx(LoadingJourney, {
        stage: "Reading failure analysis\u2026",
        progress: 0.85,
        variant: "compact"
      })
    });
  }
  if (!attention || attention.items.length === 0) return null;
  var totals = attention.totals,
    items = attention.items;
  return /*#__PURE__*/_jsxs("section", {
    className: styles.card,
    "aria-label": "Need your attention",
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.header,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.headerLeft,
        children: /*#__PURE__*/_jsx(LTText, {
          text: "Need your attention",
          variant: LTText.TextVariant.SUBHEADER_BOLD
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.headerRight,
        children: [/*#__PURE__*/_jsx(LTText, {
          text: "".concat(totals.failures, " failed"),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "#636C76"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.dot,
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx(LTText, {
          text: "".concat(totals.triaged, " triaged"),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "#636C76"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.dot,
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx(LTText, {
          text: "".concat(totals.untriaged, " untriaged"),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "#636C76"
        })]
      })]
    }), /*#__PURE__*/_jsx("ul", {
      className: styles.list,
      children: items.slice(0, ATTENTION_DISPLAY_ITEMS).map(function (item) {
        var _item$status, _item$ordinal;
        var inner = /*#__PURE__*/_jsxs(_Fragment, {
          children: [item.severity ? /*#__PURE__*/_jsx("span", {
            className: styles.pill,
            style: {
              backgroundColor: SEVERITY_BG[item.severity]
            },
            children: item.severity
          }) :
          /*#__PURE__*/
          /* No authored severity (older packs) — the status icon
             anchors the row instead (same leading visual as the
             Issues tab), so the semibold title never reads as a
             second heading. */
          _jsx(StatusIcon, {
            status: (_item$status = item.status) !== null && _item$status !== void 0 ? _item$status : 'failed',
            size: 16
          }), /*#__PURE__*/_jsx("span", {
            className: styles.rowTitle,
            title: item.rootCause,
            children: /*#__PURE__*/_jsx(LTText, {
              text: item.title,
              variant: LTText.TextVariant.SUBHEADER_REGULAR
            })
          }), item.triageStatus === 'triaged' ? /*#__PURE__*/_jsxs("span", {
            className: styles.action,
            children: ["View RCA", /*#__PURE__*/_jsx(ArrowRightIcon, {
              size: 16
            })]
          }) : /*#__PURE__*/_jsxs("span", {
            className: styles.action,
            children: ["Triage", /*#__PURE__*/_jsx(ArrowRightIcon, {
              size: 16
            })]
          })]
        });
        return /*#__PURE__*/_jsx("li", {
          className: styles.row,
          children: onOpenIssue ? /*#__PURE__*/_jsx("button", {
            type: "button",
            className: styles.rowButton,
            onClick: function onClick() {
              return onOpenIssue(item);
            },
            children: inner
          }) : /*#__PURE__*/_jsx("span", {
            className: styles.rowInner,
            children: inner
          })
        }, "".concat(item.testId, "-").concat((_item$ordinal = item.ordinal) !== null && _item$ordinal !== void 0 ? _item$ordinal : item.title));
      })
    })]
  });
}